// The destination country string coming from the backend is inconsistent in
// format depending on the hotel data provider: some return the full English
// name (e.g. "France"), others return the ISO 3166-1 alpha-2 code (e.g. "FR").
const COUNTRY_NAME_OR_CODE_TO_ISO_CODE: Record<string, string> = {
  SWEDEN: 'SE',
  SE: 'SE',
  DENMARK: 'DK',
  DK: 'DK',
  LATVIA: 'LV',
  LV: 'LV',
  POLAND: 'PL',
  PL: 'PL',
  'UNITED KINGDOM': 'GB',
  'GREAT BRITAIN': 'GB',
  UK: 'GB',
  GB: 'GB',
  IRELAND: 'IE',
  IE: 'IE',
  NETHERLANDS: 'NL',
  'THE NETHERLANDS': 'NL',
  NL: 'NL',
  GERMANY: 'DE',
  DE: 'DE',
};

export function normalizeCountryToIsoCode(
  country: string | undefined
): string | undefined {
  if (country === undefined) {
    return undefined;
  }

  return COUNTRY_NAME_OR_CODE_TO_ISO_CODE[country.trim().toUpperCase()];
}

export const FERRY_ELIGIBLE_DESTINATION_COUNTRIES: Record<string, string[]> = {
  'sembo.co.uk': ['GB', 'IE', 'NL'],
  'sembo.de': ['LV', 'SE'],
  'sembo.dk': ['SE'],
  'sembo.ie': ['GB'],
  'sembo.nl': ['GB'],
  'sembo.pl': ['SE'],
  'sembo.se': ['DE', 'DK', 'LV', 'PL'],
  // Latvia has no site of its own. This lookup-only domain makes eligible
  // Germany/Sweden <-> Latvia routes resolve in both directions.
  'sembo.lv': ['DE', 'SE'],
};

export function isFerryEligibleDestination(
  originDomain: string | undefined,
  destinationCountry: string | undefined
): boolean {
  const destinationIsoCode = normalizeCountryToIsoCode(destinationCountry);

  if (destinationIsoCode === undefined || originDomain === undefined) {
    return false;
  }

  const eligibleDestinations =
    FERRY_ELIGIBLE_DESTINATION_COUNTRIES[originDomain];

  return eligibleDestinations?.includes(destinationIsoCode) ?? false;
}

const ORIGIN_DOMAIN_BY_ISO_CODE: Record<string, string> = {
  GB: 'sembo.co.uk',
  DE: 'sembo.de',
  DK: 'sembo.dk',
  IE: 'sembo.ie',
  NL: 'sembo.nl',
  PL: 'sembo.pl',
  SE: 'sembo.se',
  LV: 'sembo.lv',
};

const COUNTRY_ISO_CODE_BY_ORIGIN_DOMAIN: Record<string, string> =
  Object.fromEntries(
    Object.entries(ORIGIN_DOMAIN_BY_ISO_CODE).map(([isoCode, domain]) => [
      domain,
      isoCode,
    ])
  );

export function getOriginDomainForCountry(
  country: string | undefined
): string | undefined {
  const isoCode = normalizeCountryToIsoCode(country);

  return isoCode ? ORIGIN_DOMAIN_BY_ISO_CODE[isoCode] : undefined;
}

export function getCountryIsoCodeForOriginDomain(
  domain: string
): string | undefined {
  return COUNTRY_ISO_CODE_BY_ORIGIN_DOMAIN[domain];
}
